import { LumiDB, type HorizontalUnit } from "@lumidb/lumidb";
import earcut from "earcut";
import type * as GeoJSON from "geojson";
import * as THREE from "three";

export const UNIT_X = new THREE.Vector3(1, 0, 0);
export const UNIT_Z = new THREE.Vector3(0, 0, 1);
export const UNIT_Y = new THREE.Vector3(0, 1, 0);
export const WEB_MERCATOR_MAX = 20037508.342789244;
export const WM_EPSG = "EPSG:3857";
export const WGS84_EPSG = "EPSG:4326";
export const WGS84_MAX_LAT = 85.051129;
export const WGS84_MIN_LAT = -WGS84_MAX_LAT;
export const WGS84_MAX_LON = 180;
export const WGS84_MIN_LON = -WGS84_MAX_LON;
export const STORAGE_KEY = "lumidb-state";

export function getDisplayName(record: Record<string, { display_name: string }>, id?: string | null): string {
    return id == null ? "" : (record[id]?.display_name ?? "");
}

export type PersistedCamera =
    | { type: "perspective"; position: { x: number; y: number; z: number }; rotation: THREE.Euler }
    | {
          type: "ortho";
          position: { x: number; y: number; z: number };
          rotation: THREE.Euler;
          left: number;
          right: number;
          top: number;
          bottom: number;
          far: number;
      };

const normal = new THREE.Vector3();
const current = new THREE.Vector3();
const next = new THREE.Vector3();
const u = new THREE.Vector3();
const v = new THREE.Vector3();
export function triangulate(vertices: number[]): number[] {
    const count = vertices.length / 3;
    if (count < 3) return [];

    // Newell's method
    normal.set(0, 0, 0);
    for (let i = 0; i < count; i++) {
        current.fromArray(vertices, i * 3);
        next.fromArray(vertices, ((i + 1) % count) * 3);
        normal.x += (current.y - next.y) * (current.z + next.z);
        normal.y += (current.z - next.z) * (current.x + next.x);
        normal.z += (current.x - next.x) * (current.y + next.y);
    }

    if (normal.lengthSq() === 0) {
        return [];
    }

    normal.normalize();

    // Set up two orthonormal axes in the plane of the polygon
    const ref = Math.abs(normal.z) < 0.9 ? UNIT_Z : UNIT_X;
    u.crossVectors(ref, normal).normalize();
    v.crossVectors(normal, u);

    // Project the vertices onto the plane defined by u and v
    const projected: number[] = [];
    for (let i = 0; i < count; i++) {
        current.fromArray(vertices, i * 3);
        projected.push(current.dot(u), current.dot(v));
    }

    return earcut(projected, [], 2);
}

export type UnitSystem = "metric" | "imperial";

const METERS_PER_INTL_FOOT = 0.3048;

const SCENE_UNIT_LABELS: Record<string, string> = {
    metre: "m",
    foot: "ft",
    "US survey foot": "ftUS",
};

export function sceneUnitLabel(sceneUnit: HorizontalUnit | null): string | null {
    return sceneUnit ? (SCENE_UNIT_LABELS[sceneUnit.name] ?? sceneUnit.name) : null;
}

/** `ratio` is display units per scene unit. `exp` is 1 for a length, 2 area, 3 volume. */
export type DisplayUnits = {
    system: UnitSystem;
    ratio: number;
    label: string;
};

export function resolveDisplayUnits(system: UnitSystem, sceneUnit: HorizontalUnit | null): DisplayUnits {
    const sceneMeters = sceneUnit && sceneUnit.meters_per_unit > 0 ? sceneUnit.meters_per_unit : 1;
    const sceneLabel = sceneUnit ? SCENE_UNIT_LABELS[sceneUnit.name] : undefined;

    // A scene already in feet reports in its own feet, so readouts match the source
    // coordinates rather than drifting by the 2 ppm between survey and international.
    if (system === "imperial" && sceneLabel?.startsWith("ft")) {
        return { system, ratio: 1, label: sceneLabel };
    }

    const metersPerDisplay = system === "imperial" ? METERS_PER_INTL_FOOT : 1;
    return { system, ratio: sceneMeters / metersPerDisplay, label: system === "imperial" ? "ft" : "m" };
}

export function toDisplay(sceneValue: number, units: DisplayUnits, exp = 1) {
    return sceneValue * units.ratio ** exp;
}

export function fromDisplay(displayValue: number, units: DisplayUnits, exp = 1) {
    return displayValue / units.ratio ** exp;
}

export function metersToScene(meters: number, sceneUnit: HorizontalUnit | null, exp = 1) {
    return meters / (sceneUnit?.meters_per_unit ?? 1) ** exp;
}

export function sceneToMeters(sceneValue: number, sceneUnit: HorizontalUnit | null, exp = 1) {
    return sceneValue * (sceneUnit?.meters_per_unit ?? 1) ** exp;
}

export function humanArea(sceneValue: number, units: DisplayUnits) {
    const v = toDisplay(sceneValue, units, 2);
    if (units.system === "imperial") {
        if (v > 27_878_400) return (v / 27_878_400).toFixed(1) + " mi²";
        if (v > 43_560) return (v / 43_560).toFixed(1) + " ac";
        return v.toFixed(1) + ` ${units.label}²`;
    }
    if (v > 1_000_000) return (v / 1_000_000).toFixed(1) + " km²";
    if (v > 10_000) return (v / 10_000).toFixed(1) + " ha";
    return v.toFixed(1) + ` ${units.label}²`;
}

export function humanVolume(sceneValue: number, units: DisplayUnits, signed = false) {
    const v = toDisplay(sceneValue, units, 3);
    const abs = Math.abs(v);
    const sign = signed ? (v >= 0 ? "+" : "-") : "";
    if (units.system === "imperial") {
        if (abs >= 27) return sign + (abs / 27).toFixed(1) + " yd³";
        return sign + abs.toFixed(1) + ` ${units.label}³`;
    }
    if (abs >= 1_000_000_000) return sign + (abs / 1_000_000_000).toFixed(1) + " km³";
    if (abs >= 1_000_000) return sign + (abs / 1_000_000).toFixed(1) + `M ${units.label}³`;
    return sign + abs.toFixed(1) + ` ${units.label}³`;
}

export function humanDistance(sceneValue: number, units: DisplayUnits) {
    const v = toDisplay(sceneValue, units);
    if (units.system === "imperial") {
        if (v > 5280) return (v / 5280).toFixed(2) + " mi";
        if (v >= 1) return v.toFixed(2) + ` ${units.label}`;
        return (v * 12).toFixed(2) + " in";
    }
    if (v > 1_000) return (v / 1_000).toFixed(2) + " km";
    if (v >= 1) return v.toFixed(2) + ` ${units.label}`;
    return (v * 100).toFixed(2) + " cm";
}

export function preciseDistance(sceneValue: number, units: DisplayUnits) {
    return toDisplay(sceneValue, units).toFixed(4) + ` ${units.label}`;
}

export function preciseArea(sceneValue: number, units: DisplayUnits) {
    return toDisplay(sceneValue, units, 2).toFixed(2) + ` ${units.label}²`;
}

export function preciseVolume(sceneValue: number, units: DisplayUnits, signed = false) {
    const v = toDisplay(sceneValue, units, 3);
    const abs = Math.abs(v);
    const sign = signed ? (v >= 0 ? "+" : "-") : "";
    return sign + abs.toFixed(2) + ` ${units.label}³`;
}

export function humanDuration(durationMs: number) {
    if (durationMs > 1_000) return (durationMs / 1_000).toFixed(2) + "s";
    return durationMs.toFixed(0) + "ms";
}

export function humanCount(count: number) {
    if (count > 1_000_000) return (count / 1_000_000).toFixed(2) + "M";
    if (count > 1_000) return (count / 1_000).toFixed(2) + "k";
    return count.toFixed(0);
}

export function humanSize(bytes: number) {
    if (bytes > 1024 * 1024 * 1024) return (bytes / (1024 * 1024 * 1024)).toFixed(1) + " GB";
    if (bytes > 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + " MB";
    if (bytes > 1024) return (bytes / 1024).toFixed(2) + " kB";
    return bytes + " B";
}

export function humanSizeEstimate(bytes: number) {
    const MB = 1024 * 1024;
    const KB = 1024;

    let step = 10 * KB;
    if (bytes > 1024 * 1024 * 1024) {
        step = 100 * MB;
    } else if (bytes > 1024 * 1024) {
        step = 10 * MB;
    }

    return humanSize(Math.ceil(bytes / step) * step);
}

export function getEnvVar(key: string): string | null {
    let value = window.__ENV__[key];

    if (!value || value.startsWith("${")) {
        value = import.meta.env[key];
    }

    if (typeof value !== "string" || value.length === 0) {
        return null;
    }

    return value;
}

// A server on this machine stays reachable when the laptop has no network.
export const isOnline = () =>
    navigator.onLine || ["localhost", "127.0.0.1", "[::1]"].includes(window.location.hostname);

export const clearArray = (arr: unknown[]) => {
    arr.length = 0;
};

export const setArray = <T>(arr: T[], next: readonly NoInfer<T>[]) => {
    arr.length = next.length;
    for (let i = 0; i < next.length; i++) {
        arr[i] = next[i];
    }
};

export const filterObject = (
    object: Record<string, unknown>,
    excludedKeys: readonly string[],
): Record<string, unknown> => Object.fromEntries(Object.entries(object).filter(([key]) => !excludedKeys.includes(key)));

export const isEmptyObject = (object: object): boolean => {
    return Object.keys(object).length === 0;
};

export const setRecord = <T extends object>(obj: T, next: T) => {
    for (const k of Object.keys(obj)) {
        if (!(k in next)) {
            delete (obj as Record<string, unknown>)[k];
        }
    }
    Object.assign(obj, next);
};

export const debounce = <T extends unknown[]>(fn: (...args: T) => void | Promise<void>, delay: number = 300) => {
    let timeout: number | undefined = undefined;

    const debounced = (...args: T) => {
        if (timeout === undefined) {
            fn(...args);
        } else {
            clearTimeout(timeout);
        }

        timeout = window.setTimeout(() => {
            timeout = undefined;
            fn(...args);
        }, delay);
    };

    debounced.cancel = () => {
        clearTimeout(timeout);
        timeout = undefined;
    };

    return debounced;
};

export const throttle = <T extends unknown[]>(fn: (...args: T) => void | Promise<void>, delay: number = 300) => {
    let lastExecuted = 0;
    let timeout: number | undefined;

    const throttled = (...args: T) => {
        const dt = performance.now() - lastExecuted;

        if (dt >= delay) {
            clearTimeout(timeout);
            timeout = undefined;
            lastExecuted = performance.now();
            fn(...args);
        } else {
            clearTimeout(timeout);
            timeout = window.setTimeout(() => {
                timeout = undefined;
                lastExecuted = performance.now();
                fn(...args);
            }, delay - dt);
        }
    };

    throttled.cancel = () => {
        clearTimeout(timeout);
        timeout = undefined;
    };

    return throttled;
};

export async function downloadOriginalFile(
    lumidb: LumiDB,
    file: { datasetName: string; sourceFileId: number; isRaster: boolean },
) {
    const links = file.isRaster
        ? [await lumidb.getRasterDownloadUrl(file.datasetName, file.sourceFileId)]
        : Object.values(
              await lumidb.getDownloadUrls({
                  tableDownloads: [{ tableName: file.datasetName, files: [file.sourceFileId] }],
              }),
          ).flat();

    const link = document.createElement("a");
    document.body.appendChild(link); // Required for Firefox
    for (const { url, filename } of links) {
        link.href = url;
        link.download = filename;
        link.click();
    }
    document.body.removeChild(link);
}

export function downloadGeoJson(name: string, features: GeoJSON.Feature<GeoJSON.Geometry | null>[]) {
    const collection: GeoJSON.FeatureCollection<GeoJSON.Geometry | null> = { type: "FeatureCollection", features };
    const blob = new Blob([JSON.stringify(collection, null, 2)], { type: "application/geo+json" });
    downloadBlob(`${name.trim() || "features"}.geojson`, blob);
}

export function downloadJson(name: string, value: unknown) {
    const blob = new Blob([JSON.stringify(value, null, 2)], { type: "application/json" });
    downloadBlob(`${name.trim() || "metadata"}.json`, blob);
}

function downloadBlob(filename: string, blob: Blob) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    document.body.appendChild(link); // Required for Firefox
    link.href = url;
    link.download = filename;
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

export function capitalize(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1);
}

export function formatDate(timestamp: string): string {
    return new Date(timestamp).toLocaleDateString("en-GB", {
        timeZone: "UTC",
        day: "numeric",
        month: "short",
        year: "numeric",
    });
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** Human-friendly "time ago" for a timestamp. Returns "Never" when null, relative
 *  text for the last day ("6 minutes ago"), a short date ("Fri 16.04") beyond that,
 *  and a dated year ("16.04.2023") when over a year ago. */
export function timeAgo(timestamp: string | null): string {
    if (timestamp === null) return "Never";

    const then = new Date(timestamp);
    const time = then.getTime();
    if (Number.isNaN(time)) return "Never";

    const seconds = (Date.now() - time) / 1000;

    if (seconds < 60) return "Just now";

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;

    const day = String(then.getDate()).padStart(2, "0");
    const month = String(then.getMonth() + 1).padStart(2, "0");

    if (seconds >= 365 * 24 * 60 * 60) {
        return `${day}.${month}.${then.getFullYear()}`;
    }

    const weekday = WEEKDAYS[then.getDay()];
    return `${weekday} ${day}.${month}`;
}

export const unitToMeters: Record<string, number> = {
    mm: 0.001,
    cm: 0.01,
    m: 1,
    in: 0.0254,
    ft: 0.3048,
    yd: 0.9144,
};

export const distanceUnits = {
    metric: ["m", "cm", "mm"],
    imperial: ["yd", "ft", "in"],
};

export const convertUnits = (value: number, from: string, to: string) => {
    return value * (unitToMeters[from] / unitToMeters[to]);
};

export function getProjectId() {
    const parts = window.location.pathname.split("/");
    const projectId = parts.length > 2 && parts[1] === "projects" && parts[2] !== "" ? parts[2] : null;

    if (projectId === null) {
        throw new Error("Project ID not found");
    }

    return projectId;
}

const originalReplaceState = history.replaceState.bind(history);
history.replaceState = function (...args) {
    originalReplaceState(...args);
    window.dispatchEvent(new Event("replacestate"));
};

export const getRem = () => {
    const documentElement = document.documentElement;
    return parseFloat(getComputedStyle(documentElement).fontSize);
};

export function serializeCamera(camera: THREE.Camera): PersistedCamera {
    if (camera instanceof THREE.OrthographicCamera) {
        return {
            type: "ortho",
            position: camera.position,
            rotation: camera.rotation,
            left: camera.left,
            right: camera.right,
            top: camera.top,
            bottom: camera.bottom,
            far: camera.far,
        };
    }

    return { type: "perspective", position: camera.position, rotation: camera.rotation };
}
